import React, { useState } from "react";
import SectionHeader from "./SectionHeader";
import { SearchBar } from "@/components/SearchBox/SearchBar";
import { getAddressAPI } from "@/redux/Purchase/actions";

function ShipperConsignee({
    expandedSections,
    onChange = () => { },
    toggleSection,
    formData,
    labelClass,
    inputClass,
    errors = {},           // ← accept errors from parent
    setErrors = () => { }, // ← accept setErrors from parent
}) {
    const _setAddress = async (type, id) => {
        const address = await getAddressAPI(id);
        onChange(type, address, "text");
    };

    // Validate shipper on blur (if parent doesn't pass errors)
    const [localErrors, setLocalErrors] = useState({});
    const activeErrors = Object.keys(errors).length > 0 ? errors : localErrors;
    const activeSetErrors = Object.keys(errors).length > 0 ? setErrors : setLocalErrors;

    const validateShipper = () => {
        if (!formData?.shipper || formData.shipper.trim() === "") {
            activeSetErrors((prev) => ({
                ...prev,
                shipper: "Shipper / Exporter is required.",
            }));
        } else {
            activeSetErrors((prev) => {
                const next = { ...prev };
                delete next.shipper;
                return next;
            });
        }
    };

    return (
        <div className="grid grid-cols-2 gap-4">
            {/* Shipper & Consignee */}
            <div className="rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 space-y-4">
                    <div>
                        <label className={labelClass}>
                            Shipper/Exporter:
                            <span className="text-red-500"> * </span>
                        </label>

                        <SearchBar
                            searchAPI={"v1/AutoSuggestion/GetConsigneeSuggestion?Prefix="}
                            selectReacord={(d) => {
                                onChange("shipper", d?.[1], "text");
                                _setAddress("shipperaddress", d?.[0]);
                                // Clear error on valid selection
                                activeSetErrors((prev) => {
                                    const next = { ...prev };
                                    delete next.shipper;
                                    return next;
                                });
                            }}
                            onchange={(val) => {
                                if (!val) {
                                    onChange("shipper", "", "text");
                                    // Show error when cleared
                                    activeSetErrors((prev) => ({
                                        ...prev,
                                        shipper: "Shipper / Exporter is required.",
                                    }));
                                }
                            }}
                            onBlur={validateShipper}
                            value={formData?.shipper || ""}
                        />

                        {/* Error message — same pattern as ImportBookingForm */}
                        {activeErrors.shipper && (
                            <p className="text-red-500 text-xs mt-1">
                                {activeErrors.shipper}
                            </p>
                        )}

                        <div>
                            <textarea
                                rows={4}
                                className={`${inputClass} mt-2`}
                                value={formData?.shipperaddress}
                                onChange={(e) =>
                                    onChange("shipperaddress", e.target.value, "text")
                                }
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className={`${labelClass} mb-0`}>Consignee:</label>
                            <label className="flex items-center gap-1 text-xs text-slate-500">
                                <input
                                    type="checkbox"
                                    className="rounded"
                                    name="hidecneeonprint"
                                    checked={formData?.hidecneeonprint}
                                    onChange={(e) =>
                                        onChange("hidecneeonprint", e.target.checked, "checkbox")
                                    }
                                />
                                Hide On Print
                            </label>
                        </div>

                        <SearchBar
                            searchAPI={"v1/AutoSuggestion/GetConsigneeSuggestion?Prefix="}
                            selectReacord={(d) => {
                                onChange("consignee", d?.[1], "text");
                                _setAddress("consigneeaddress", d?.[0]);
                            }}
                            onchange={(val) => {
                                if (!val) {
                                    onChange("consignee", "", "text");
                                }
                            }}
                            value={formData?.consignee || ""}
                        />

                        <textarea
                            rows={4}
                            className={`${inputClass} mt-2`}
                            value={formData?.consigneeaddress}
                            name="consigneeaddress"
                            onChange={(e) =>
                                onChange("consigneeaddress", e.target.value, "text")
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Forwarding Agent & Notify */}
            <div className="rounded border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="p-4 space-y-4">
                    <div>
                        <label className={labelClass}>Forwarding Agent:</label>
                        <SearchBar
                            searchAPI={"v1/AutoSuggestion/GetConsigneeSuggestion?Prefix="}
                            selectReacord={(d) => {
                                onChange("forwarder", d?.[1], "text");
                                _setAddress("forwarderaddress", d?.[0]);
                            }}
                            onchange={(val) => {
                                if (!val) {
                                    onChange("forwarder", "", "text");
                                }
                            }}
                            value={formData?.forwarder || ""}
                        />

                        <textarea
                            rows={4}
                            name="forwarderaddress"
                            className={`${inputClass} mt-2`}
                            value={formData?.forwarderaddress}
                            onChange={(e) =>
                                onChange("forwarderaddress", e.target.value, "text")
                            }
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className={`${labelClass} mb-0`}>Notify:</label>
                            <label className="flex items-center gap-1 text-xs text-slate-500">
                                <input
                                    type="checkbox"
                                    className="rounded"
                                    name="hidenotifyonprint"
                                    checked={formData?.hidenotifyonprint}
                                    onChange={(e) =>
                                        onChange(
                                            "hidenotifyonprint",
                                            e.target.checked,
                                            "checkbox",
                                        )
                                    }
                                />
                                Hide On Print
                            </label>
                        </div>

                        <SearchBar
                            searchAPI={"v1/AutoSuggestion/GetConsigneeSuggestion?Prefix="}
                            selectReacord={(d) => {
                                onChange("notify", d?.[1], "text");
                                _setAddress("notifyaddress", d?.[0]);
                            }}
                            onchange={(val) => {
                                if (!val) {
                                    onChange("notify", "", "text");
                                }
                            }}
                            value={formData?.notify || ""}
                        />

                        <textarea
                            rows={4}
                            name="notifyaddress"
                            className={`${inputClass} mt-2`}
                            value={formData?.notifyaddress}
                            onChange={(e) =>
                                onChange("notifyaddress", e.target.value, "text")
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ShipperConsignee;